import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as UserRolesActions from './user-roles.actions';
import { UserRolesStoreService } from './user-roles.service';
import { ArkikAlertService } from 'src/app/shared/components/arkik-alert/arkik-alert.service';
import { TranslationService } from 'src/app/core/services/common/translation.service';
import { map, switchMap, catchError } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectUserRolesByGuid } from './user-roles.selectors';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { IdbCatalogName } from 'src/app/idb/idb-catalog-name.enum';

@Injectable()
export class UserRolesEffects {
  constructor(
    private actions$: Actions,
    private userRolesService: UserRolesStoreService,
    private alert: ArkikAlertService,
    private translation: TranslationService,
    private store: Store,
    private cookieService: CookieService,
    private idb: ManageIdbService,
  ) {}

  loadUserRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserRolesActions.loadUserRoles),
      switchMap(() => {
        const userGuid = this.cookieService.get(environment.UserGuid)?.toUpperCase() || '';
        return this.store.select(selectUserRolesByGuid(userGuid)).pipe(
          switchMap((roles) => {
            if (roles) {
              return of(UserRolesActions.loadUserRolesSuccess({ userGuid, roles }));
            }

            return from(this.idb.select(IdbCatalogName.UserRoles)).pipe(
              switchMap((cachedRoles) => {
                if (cachedRoles?.length) {
                  return of(UserRolesActions.loadUserRolesSuccess({ userGuid, roles: cachedRoles }));
                }
                this.store.dispatch(UserRolesActions.loadingUserRoles({ loading: true }));
                return this.userRolesService.getUserRoles().pipe(
                  switchMap((response) => from(this.idb.saveUserRoles(response)).pipe(map(() => response))),
                  map((response) => UserRolesActions.loadUserRolesSuccess({ userGuid, roles: response })),
                  catchError((e) => {
                    this.alert.error(e?.error?.message || this.translation.pt('views.globals.an-error-occurred'));
                    return of(
                      UserRolesActions.loadUserRolesFailure({
                        error: e?.error?.message || this.translation.pt('views.globals.an-error-occurred'),
                      }),
                    );
                  }),
                );
              }),
            );
          }),
        );
      }),
    ),
  );

  refreshUserRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserRolesActions.refreshUserRoles),
      map(() => UserRolesActions.resetUserRoles()),
    ),
  );
}
